/**
 * LinkUserModal — Modal de 2 pasos para vincular un borrador importado
 * a un usuario real de Firebase.
 *
 * Paso 1: Buscar usuario (por nombre o email)
 * Paso 2: Confirmar y vincular (muestra resumen antes de escribir)
 */
import { useState, useEffect, useMemo } from "react";
import { db } from "@/firebase";
import { collection, getDocs } from "firebase/firestore";
import { linkDraftToUser } from "@/services/importedDrafts";
import {
  X,
  Search,
  User,
  CheckCircle,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Link,
} from "lucide-react";

export default function LinkUserModal({ draft, onClose, onLinked }) {
  const [step, setStep] = useState(1); // 1 = buscar, 2 = confirmar
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Cargar todos los usuarios de Firebase (misma lógica que AdminUsers)
  useEffect(() => {
    getDocs(collection(db, "users"))
      .then((snap) => {
        const list = snap.docs.map((d) => ({
          uid: d.id,
          email: d.data().email || d.data().profile?.email || "",
          displayName:
            d.data().displayName ||
            [d.data().profile?.nombres, d.data().profile?.apellidos]
              .filter(Boolean)
              .join(" ") ||
            d.data().email ||
            "Sin nombre",
          isActive: d.data().isActive !== false,
        }));
        setUsers(list.filter((u) => u.isActive));
      })
      .catch(() => setError("No se pudo cargar la lista de usuarios"))
      .finally(() => setLoadingUsers(false));
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return users.slice(0, 30);
    const s = search.toLowerCase();
    return users.filter(
      (u) =>
        u.displayName.toLowerCase().includes(s) ||
        u.email.toLowerCase().includes(s)
    );
  }, [search, users]);

  async function handleConfirm() {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const result = await linkDraftToUser(
        draft.id,
        draft.tarjeta,
        selected.uid,
        { email: selected.email, displayName: selected.displayName },
        draft.measurements
      );
      onLinked({
        draftId: draft.id,
        linkedUid: selected.uid,
        linkedEmail: selected.email,
        linkedName: selected.displayName,
        measurementsWritten: result.measurementsWritten,
      });
    } catch (e) {
      setError(e.message || "Error al vincular");
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      data-testid="link-user-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
        style={{ background: "#FFFFFF" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: "#F1F5F9" }}
        >
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button
                type="button"
                onClick={() => { setStep(1); setError(null); }}
                className="mr-1 text-slate-400 hover:text-slate-600"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <Link size={16} style={{ color: "#00628F" }} />
            <span className="font-semibold text-sm" style={{ color: "#1E293B" }}>
              {step === 1 ? "Buscar usuario" : "Confirmar vínculo"}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100"
          >
            <X size={15} style={{ color: "#94A3B8" }} />
          </button>
        </div>

        {/* Draft info */}
        <div
          className="px-5 py-3 border-b"
          style={{ background: "#F8FAFC", borderColor: "#F1F5F9" }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: "#94A3B8" }}>
            Borrador a vincular
          </p>
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
              style={{
                background: draft.sexo === "Femenino" ? "#FCE7F3" : "#DBEAFE",
                color: draft.sexo === "Femenino" ? "#BE185D" : "#1D4ED8",
              }}
            >
              {(draft.nombres[0] || "").toUpperCase()}
              {(draft.apellidos[0] || "").toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: "#1E293B" }}>
                {draft.nombres} {draft.apellidos}
              </p>
              <p className="text-[11px]" style={{ color: "#64748B" }}>
                FN: {draft.fechaNacimiento} · {draft.sexo || "Sin dato"} · {draft.totalMeasurements} medición(es)
              </p>
            </div>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-5 mt-3 px-3 py-2 rounded-lg flex items-center gap-2 text-xs" style={{ background: "#FEF2F2", color: "#991B1B" }}>
            <AlertCircle size={13} />
            {error}
          </div>
        )}

        {/* ── PASO 1: Buscar usuario ── */}
        {step === 1 && (
          <div className="p-5">
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }} />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm"
                style={{ borderColor: "#E2E8F0", color: "#1E293B", outline: "none" }}
                data-testid="link-modal-search"
              />
            </div>

            <div
              className="rounded-xl border overflow-y-auto"
              style={{ borderColor: "#E2E8F0", maxHeight: 280 }}
            >
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8 gap-2 text-xs" style={{ color: "#94A3B8" }}>
                  <Loader2 size={14} className="animate-spin" /> Cargando usuarios…
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-8 text-xs" style={{ color: "#94A3B8" }}>
                  No se encontraron usuarios
                </div>
              ) : (
                filtered.map((u) => (
                  <button
                    key={u.uid}
                    type="button"
                    onClick={() => { setSelected(u); setStep(2); setError(null); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-b last:border-b-0 hover:bg-slate-50"
                    style={{ borderColor: "#F1F5F9" }}
                    data-testid={`link-user-option-${u.uid}`}
                  >
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                      style={{ background: "#E0F2FE", color: "#0284C7" }}
                    >
                      <User size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: "#1E293B" }}>
                        {u.displayName}
                      </p>
                      <p className="text-[11px] truncate" style={{ color: "#64748B" }}>
                        {u.email}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
            <p className="text-[11px] mt-2 text-center" style={{ color: "#94A3B8" }}>
              {filtered.length} usuario(s) · haz clic para seleccionar
            </p>
          </div>
        )}

        {/* ── PASO 2: Confirmar ── */}
        {step === 2 && selected && (
          <div className="p-5">
            <div
              className="rounded-xl border p-4 mb-4"
              style={{ borderColor: "#BBF7D0", background: "#F0FDF4" }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "#166534" }}>
                Usuario seleccionado
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <User size={14} style={{ color: "#16A34A" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "#1E293B" }}>
                    {selected.displayName}
                  </p>
                  <p className="text-[11px]" style={{ color: "#64748B" }}>{selected.email}</p>
                </div>
              </div>
            </div>

            <div
              className="rounded-xl border p-4 mb-4 text-xs"
              style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}
            >
              <p className="font-semibold mb-1.5" style={{ color: "#475569" }}>Al confirmar se escribirá:</p>
              <ul className="space-y-1" style={{ color: "#64748B" }}>
                <li>• <strong>{draft.totalMeasurements}</strong> medición(es) en el historial del usuario</li>
                <li>• Registro de vínculo en la colección <code className="text-[10px] bg-slate-200 px-1 rounded">importedDrafts</code></li>
              </ul>
              <p className="mt-2 text-[10px]" style={{ color: "#94A3B8" }}>
                Las mediciones se marcarán como "importadas" con su fecha original.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setSelected(null); }}
                disabled={saving}
                className="py-2.5 rounded-xl text-sm font-semibold border transition-colors"
                style={{ borderColor: "#E2E8F0", color: "#64748B", background: "white" }}
              >
                Cambiar
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving}
                className="py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                style={{
                  background: "linear-gradient(135deg,#00628F,#2A9D8F)",
                  color: "white",
                  cursor: saving ? "wait" : "pointer",
                }}
                data-testid="link-modal-confirm-btn"
              >
                {saving ? (
                  <><Loader2 size={14} className="animate-spin" /> Vinculando…</>
                ) : (
                  <><CheckCircle size={14} /> Confirmar</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
